import { useMemo } from "react";
import { toast } from "sonner";

import CloudinaryImage from "@/components/cloudinary-image";
import CommunityScoreWheel from "@/components/community-score-wheel";
import GameCategories from "@/components/game-categories";
import { Button } from "@/components/ui/button";
import { gameUrl } from "@/helpers/games";
import { bookingLinkProps } from "@/helpers/url";
import { cn } from "@/lib/utils";

import type { GameType } from "../../types";
import FeaturedReview from "../components/featured-review";
import { AwardIcon } from "../icons";
import CopyNotification from "./copy-notification";
import { LocationIcon, ShareIcon } from "./icons";

interface CardProps extends React.ComponentProps<"div"> {
  className?: string;
  game: GameType;
  isActive: boolean;
}

export default function Card({
  className,
  game,
  isActive,
  ...props
}: CardProps) {
  const url = useMemo(() => gameUrl(game), [game]);

  const handleShare = () => {
    navigator.clipboard.writeText(process.env.APP_URL + url);
    toast.custom((id) => (
      <CopyNotification onClose={() => toast.dismiss(id)} />
    ));
  };

  const bookingProps = bookingLinkProps(game);

  return (
    <div
      className={cn(
        "relative rounded-l-[10px] p-6 shadow-black/30 transition-[colors,shadow]",
        'after:absolute after:inset-y-0 after:right-0 after:w-[5px] after:rounded-[3px] after:bg-linear-180 after:from-peach after:to-pale-gold after:opacity-0 after:transition-opacity after:content-[""]',
        isActive && "bg-white/7 shadow-0-0-20 after:opacity-100",
        className
      )}
      {...props}
    >
      <div className="flex items-start gap-5">
        <div className="relative aspect-square w-[32%] shrink-0 overflow-hidden rounded-[5px] bg-white">
          <CloudinaryImage className="size-full" game={game} rW={654} />
          {!!game.awards.length && (
            <AwardIcon className="absolute top-0 left-1.5 aspect-[44/60] h-auto w-[30%] max-w-10 md:left-2.5" />
          )}
        </div>
        <div className="flex grow-1 flex-col items-start gap-[7px]">
          <div className="flex w-full justify-between">
            <h2 className="grow-1 font-medium text-24/[1.5em] text-white">
              <a
                className="link-overlay"
                href={url}
                rel="noopener"
                target="_blank"
              >
                {game.name}
              </a>
            </h2>
            <Button
              aria-label="Share"
              className={cn(
                "relative ml-2.5 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-0-10-20 shadow-black/6 transition-colors",
                'after:absolute after:inset-0 after:bg-linear-to-b after:from-bright-pink after:to-dark-magenta after:opacity-0 after:transition-opacity after:content-[""]',
                'before:absolute before:inset-[3px] before:z-1 before:rounded-full before:bg-white before:content-[""]',
                "hover:bg-white hover:after:opacity-100"
              )}
              onClick={handleShare}
            >
              <ShareIcon className="relative z-2" />
            </Button>
          </div>
          <h3 className="font-medium text-16/[1.2em] text-pale-lavender">
            {game.location.company.name}
          </h3>
          {!!game.communityRatingCount &&
            !!game.communityScoreBucket &&
            !!game.communityScorePercentages && (
              <p className="flex items-center gap-[5px] text-14/[1.29em] text-white">
                <CommunityScoreWheel
                  className="size-[21px]"
                  scores={game.communityScorePercentages}
                />
                {game.communityScoreBucket}
              </p>
            )}
          {game.location.address && (
            <p className="flex items-center gap-1.5 text-12/[1.5em] text-pale-lavender">
              <LocationIcon />
              {game.location.address.formatted_address}
            </p>
          )}
          <GameCategories
            categories={[game.primaryCategory, ...game.secondaryCategories]}
            className="gap-2"
          />
        </div>
      </div>
      {!!game.featuredReview && <FeaturedReview review={game.featuredReview} />}
      {!!bookingProps && (
        <Button
          asChild
          className="relative mt-5 h-[45px] w-full rounded-[23px]"
          variant="cta"
        >
          <a {...bookingProps}>Book now</a>
        </Button>
      )}
    </div>
  );
}
